import { useCallback, useEffect, useState } from 'react';

/**
 * Light and dark, defaulting to light.
 *
 * The brand does not simply invert. Against white, lime scores 1.7:1 and gold
 * 1.8:1 — unreadable as text — while purple reaches 11.9:1. Against near-black
 * the position reverses: purple falls to 1.6:1 and lime rises to 11.7:1. So the
 * two themes do not share an accent colour; they swap which brand colour
 * carries interaction and which is reduced to a surface. That is why the themes
 * are two token sets rather than one set with a filter over it.
 *
 * The choice is remembered per browser. It is a display preference, so
 * localStorage is right: it never needs to reach the server, and losing it
 * costs the user one click.
 */

export type Theme = 'light' | 'dark';
const STORAGE_KEY = 'ge-theme';

export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // Private browsing, or storage blocked. Fall through to the default.
  }
  // Deliberately NOT prefers-color-scheme. The default is light because the
  // screens are dense data tables, which is what was asked for.
  return 'light';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Not fatal: the theme still applies for this session.
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === 'light' ? 'dark' : 'light'));
  }, []);

  return { theme, toggle };
}
